import { Component, OnInit, inject, signal, computed, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { firstValueFrom } from 'rxjs';

import { NzTableModule } from 'ng-zorro-antd/table';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzButtonModule } from 'ng-zorro-antd/button';

import { MarkdownPreviewComponent } from '@rapider/markdown-preview';
import { Definition, DefinitionControllerService } from '@rapider/sdk';
import { NotificationService } from '@rapider/services';
import { selectProjectModelsList } from '@rapider/project-data';

import {
  ONBOARDING_CONFIG,
  buildLocalGraphData,
  buildGlobalGraphData,
  initializeExpandedState,
  toggleRecordState,
  hasDetailedAiConfig,
  buildProcessTemplatePayload
} from '../onboarding.config';
import { DataModelsDiagramComponent } from '../data-models-diagram/data-models-diagram.component';


/**
 * @class DataTemplatesPreviewComponent
 * @description Provides a comprehensive view of a Data Template. 
 * Can function as a routed smart component OR a dumb presentation component via Inputs.
 */
@Component({
  selector: 'rapider-data-templates-preview',
  standalone: true,
  imports: [
    CommonModule,
    NzTableModule,
    NzTagModule,
    NzModalModule,
    NzButtonModule,
    DataModelsDiagramComponent,
    MarkdownPreviewComponent,

  ],
  templateUrl: './data-templates-preview.component.html',
  styleUrls: ['./data-templates-preview.component.scss'],
  host: {
    class: 'block h-full w-full bg-body-background overflow-y-auto custom-scrollbar'
  }
})
export class DataTemplatesPreviewComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly store = inject(Store);
  private readonly definitionController = inject(DefinitionControllerService);
  private readonly notificationService = inject(NotificationService);

  public readonly config = ONBOARDING_CONFIG;
  public readonly checkAiConfig = hasDetailedAiConfig;

  public currentProjectModels = computed(() => this.store.selectSignal(selectProjectModelsList)() || []);

  // --- Input Bindings for Reusability ---

  /** * Directly sets the data without fetching via API. 
   * Triggers initialization logic cleanly through the setter.
   * Supports both nested 'value.models' (from DB Definition) and flat 'models' (from AI Generation).
   */
  @Input() set previewData(data: any) {
    if (data) {
      this.templateData.set(data);

      // CRITICAL FIX: Support both Definition structures and raw AI generation output arrays
      const models = data.value?.models || data.models;

      if (Array.isArray(models)) {
        this.modelsList.set(models);
        this.expandedModels.set(initializeExpandedState(models));
      }
      this.isLoading.set(false);
    }
  }

  /** Flag to hide all routing and Action buttons. */
  @Input() hideActions: boolean = false;

  /** Flag to explicitly hide only the "Back" routing button. */
  @Input() hideBackButton: boolean = false;

  /** Flag to explicitly hide only the "Integrate" action button. */
  @Input() hideIntegrateButton: boolean = false;

  /**
   * @property {boolean} hideHeader
   * @description Flag to hide the top title banner and cover page sections.
   * Useful when embedding this view inside larger wrapper pages (like the App Store Detail) to prevent redundant headers.
   */
  @Input() hideHeader: boolean = false;

  // --- Data Signals ---
  public templateData = signal<any>(null);
  public modelsList = signal<any[]>([]);

  // Tracks the expanded/collapsed state of each model card
  public expandedModels = signal<Record<string, boolean>>({});

  // Pre-computes the individual local graph maps for every model
  public graphDataMap = computed(() => {
    const models = this.modelsList();
    const map = new Map<string, { nodes: any[], edges: any[] }>();
    if (models && models.length > 0) {
      models.forEach(m => {
        map.set(m.name, buildLocalGraphData(m.name, models));
      });
    }
    return map;
  });

  // Pre-computes the comprehensive global architecture graph
  public globalGraphData = computed(() => buildGlobalGraphData(this.modelsList()));

  // UI State Signals
  public isLoading = signal<boolean>(true);
  public isProcessing = signal<boolean>(false); // Used only if natively processing without the wizard

  ngOnInit(): void {
    // If previewData was injected natively, skip routing logic
    if (this.templateData()) {
      return;
    }

    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.fetchTemplateDetails(id);
    } else {
      this.router.navigate(['/data-templates']);
    }
  }

  /**
   * Fetches the full definition detail payload from the API.
   * Initializes the expanded state of all returned models via a pure function.
   * @param {string} id - The definition ID.
   */
  private fetchTemplateDetails(id: string): void {
    this.isLoading.set(true);

    this.definitionController.findById({ id }).subscribe({
      next: (res: any) => {
        this.templateData.set(res);
        if (res.value && Array.isArray(res.value.models)) {
          this.modelsList.set(res.value.models);
          this.expandedModels.set(initializeExpandedState(res.value.models));
        }
        this.isLoading.set(false);
      },
      error: () => {
        this.notificationService.createNotification('error', 'Error', this.config.notifications.fetchPreviewFailed);
        this.isLoading.set(false);
        this.router.navigate(['/data-templates']);
      }
    });
  }

  /**
   * Toggles the collapse state of a specific model card.
   * @param {string} modelName - The name of the model to toggle.
   */
  public toggleModelExpansion(modelName: string): void {
    this.expandedModels.update(state => toggleRecordState(state, modelName));
  }

  /**
   * Expands the target model card (if collapsed) and smooth scrolls to it.
   * @param {string} modelName - The name of the target model.
   */
  public scrollToModel(modelName: string): void {
    // Ensure the model is expanded first
    this.expandedModels.update(state => ({ ...state, [modelName]: true }));

    // Timeout required to allow Angular to render the potentially collapsed elements before calculating offset
    setTimeout(() => {
      const element = document.getElementById(`model-card-${modelName}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  }

  /**
   * Navigates the user to the standalone integration workflow page.
   */
  public startIntegrationFlow(): void {
    const template = this.templateData();
    if (template && template.id) {
      this.router.navigate(['/data-templates', template.id, 'integrate']);
    }
  }

  /**
   * Helper to navigate backwards.
   */
  public goBack(): void {
    this.router.navigate(['/data-templates']);
  }
}